// Pure helpers for the color/design + text tools cluster.
// No dependencies beyond the shared color math. Safe to import on server or client.

import { rgbToHex, type Rgb } from './browser-color-tools';

export type { Rgb };
export { rgbToHex };

/* ------------------------------------------------------------------ */
/* Font pairing                                                        */
/* ------------------------------------------------------------------ */

export type FontCategory = 'serif' | 'sans' | 'mono' | 'display';

export type FontFace = {
  name: string;
  stack: string;
  category: FontCategory;
};

// Every stack starts with widely installed system / web-safe fonts, so the
// live preview renders accurately with no external font loading.
export const FONT_FACES: FontFace[] = [
  { name: 'Georgia', stack: "Georgia, 'Times New Roman', Times, serif", category: 'serif' },
  { name: 'Times New Roman', stack: "'Times New Roman', Times, serif", category: 'serif' },
  {
    name: 'Palatino',
    stack: "'Palatino Linotype', Palatino, 'Book Antiqua', Georgia, serif",
    category: 'serif',
  },
  { name: 'Cambria', stack: 'Cambria, Georgia, serif', category: 'serif' },
  { name: 'Garamond', stack: "Garamond, 'EB Garamond', Georgia, serif", category: 'serif' },
  { name: 'Arial', stack: 'Arial, Helvetica, sans-serif', category: 'sans' },
  {
    name: 'Helvetica Neue',
    stack: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    category: 'sans',
  },
  { name: 'Verdana', stack: 'Verdana, Geneva, sans-serif', category: 'sans' },
  { name: 'Tahoma', stack: 'Tahoma, Geneva, Verdana, sans-serif', category: 'sans' },
  {
    name: 'Trebuchet MS',
    stack: "'Trebuchet MS', 'Segoe UI', Tahoma, sans-serif",
    category: 'sans',
  },
  {
    name: 'Segoe UI',
    stack: "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    category: 'sans',
  },
  {
    name: 'System UI',
    stack: "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
    category: 'sans',
  },
  { name: 'Calibri', stack: "Calibri, 'Segoe UI', Arial, sans-serif", category: 'sans' },
  { name: 'Consolas', stack: "Consolas, 'SF Mono', Menlo, Monaco, monospace", category: 'mono' },
  { name: 'Courier New', stack: "'Courier New', Courier, monospace", category: 'mono' },
  { name: 'Menlo', stack: "Menlo, Monaco, Consolas, 'Courier New', monospace", category: 'mono' },
  {
    name: 'Impact',
    stack: "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
    category: 'display',
  },
  {
    name: 'Copperplate',
    stack: "Copperplate, 'Copperplate Gothic Light', Georgia, serif",
    category: 'display',
  },
  { name: 'Comic Sans MS', stack: "'Comic Sans MS', 'Comic Sans', cursive", category: 'display' },
];

export type FontPair = {
  heading: FontFace;
  body: FontFace;
  note: string;
};

function faceByName(name: string): FontFace {
  const face = FONT_FACES.find((item) => item.name === name);
  if (!face) {
    throw new Error(`Unknown font face: ${name}`);
  }
  return face;
}

// Hand-picked pairings that read well together.
export const CURATED_PAIRS: FontPair[] = [
  {
    heading: faceByName('Georgia'),
    body: faceByName('Verdana'),
    note: 'Classic editorial contrast',
  },
  {
    heading: faceByName('Helvetica Neue'),
    body: faceByName('Georgia'),
    note: 'Modern sans headline over a readable serif',
  },
  {
    heading: faceByName('Trebuchet MS'),
    body: faceByName('Georgia'),
    note: 'Friendly, approachable body copy',
  },
  {
    heading: faceByName('Palatino'),
    body: faceByName('Verdana'),
    note: 'Warm serif with a clean screen sans',
  },
  {
    heading: faceByName('Cambria'),
    body: faceByName('Calibri'),
    note: 'The classic office document pairing',
  },
  {
    heading: faceByName('Arial'),
    body: faceByName('Georgia'),
    note: 'Neutral heading, timeless serif body',
  },
  {
    heading: faceByName('Impact'),
    body: faceByName('Georgia'),
    note: 'Bold display heading with a calm serif',
  },
  {
    heading: faceByName('Garamond'),
    body: faceByName('Trebuchet MS'),
    note: 'Elegant serif with a rounded sans',
  },
  {
    heading: faceByName('Segoe UI'),
    body: faceByName('Georgia'),
    note: 'Interface sans over a bookish serif',
  },
  {
    heading: faceByName('Copperplate'),
    body: faceByName('Verdana'),
    note: 'Refined small-caps title with plain body',
  },
];

// Cross-product of two categories, skipping identical faces. Capped for a tidy list.
export function generatePairs(
  headingCategory: FontCategory,
  bodyCategory: FontCategory,
): FontPair[] {
  const headings = FONT_FACES.filter((face) => face.category === headingCategory);
  const bodies = FONT_FACES.filter((face) => face.category === bodyCategory);
  const pairs: FontPair[] = [];

  for (const heading of headings) {
    for (const body of bodies) {
      if (heading.name === body.name) {
        continue;
      }
      pairs.push({
        heading,
        body,
        note: `${heading.name} heading with ${body.name} body`,
      });
      if (pairs.length >= 24) {
        return pairs;
      }
    }
  }

  return pairs;
}

// CSS snippet a designer can copy straight into a stylesheet.
export function pairToCss(pair: FontPair): string {
  return [
    '/* Heading */',
    'h1, h2, h3 {',
    `  font-family: ${pair.heading.stack};`,
    '}',
    '',
    '/* Body */',
    'body, p {',
    `  font-family: ${pair.body.stack};`,
    '}',
  ].join('\n');
}

/* ------------------------------------------------------------------ */
/* Average / dominant color                                            */
/* ------------------------------------------------------------------ */

export type ColorSwatch = {
  hex: string;
  rgb: Rgb;
  share: number; // 0-1 proportion of sampled pixels
};

// Straight mean of every non-transparent pixel.
export function computeAverageColor(data: Uint8ClampedArray): Rgb {
  let r = 0;
  let g = 0;
  let b = 0;
  let count = 0;

  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3];
    if (alpha === 0) {
      continue;
    }
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    count += 1;
  }

  if (count === 0) {
    return { r: 0, g: 0, b: 0 };
  }

  return {
    r: Math.round(r / count),
    g: Math.round(g / count),
    b: Math.round(b / count),
  };
}

type Bucket = { r: number; g: number; b: number; count: number };

// Simple color quantization: fold each channel into 16 levels, tally the
// buckets, then return the most common ones with their true average color.
export function computeDominantPalette(data: Uint8ClampedArray, maxColors = 6): ColorSwatch[] {
  const buckets = new Map<number, Bucket>();
  let total = 0;

  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3];
    if (alpha === 0) {
      continue;
    }
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.r += r;
      bucket.g += g;
      bucket.b += b;
      bucket.count += 1;
    } else {
      buckets.set(key, { r, g, b, count: 1 });
    }
    total += 1;
  }

  if (total === 0) {
    return [];
  }

  return [...buckets.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, maxColors)
    .map((bucket) => {
      const rgb: Rgb = {
        r: Math.round(bucket.r / bucket.count),
        g: Math.round(bucket.g / bucket.count),
        b: Math.round(bucket.b / bucket.count),
      };
      return { hex: rgbToHex(rgb), rgb, share: bucket.count / total };
    });
}

export function formatRgb({ r, g, b }: Rgb): string {
  return `rgb(${r}, ${g}, ${b})`;
}

/* ------------------------------------------------------------------ */
/* Anagram checking                                                    */
/* ------------------------------------------------------------------ */

export type AnagramOptions = {
  ignoreCase: boolean;
  ignoreSpaces: boolean;
  ignorePunctuation: boolean;
};

export const DEFAULT_ANAGRAM_OPTIONS: AnagramOptions = {
  ignoreCase: true,
  ignoreSpaces: true,
  ignorePunctuation: true,
};

// Reduce a phrase to the comparable characters, sorted for display.
export function normalizeForAnagram(input: string, options: AnagramOptions): string {
  let value = input.normalize('NFC');
  if (options.ignoreCase) {
    value = value.toLowerCase();
  }
  const chars = [...value].filter((char) => {
    if (options.ignoreSpaces && /\s/.test(char)) {
      return false;
    }
    if (options.ignorePunctuation && /[^\p{L}\p{N}]/u.test(char)) {
      return false;
    }
    return true;
  });
  return chars.sort((a, b) => a.localeCompare(b)).join('');
}

export function letterFrequency(normalized: string): Map<string, number> {
  const freq = new Map<string, number>();
  for (const char of normalized) {
    freq.set(char, (freq.get(char) ?? 0) + 1);
  }
  return freq;
}

export type LetterDiff = {
  letter: string;
  countA: number;
  countB: number;
};

export type AnagramResult = {
  isAnagram: boolean;
  normalizedA: string;
  normalizedB: string;
  freqA: Map<string, number>;
  freqB: Map<string, number>;
  diffs: LetterDiff[];
};

export function checkAnagram(a: string, b: string, options: AnagramOptions): AnagramResult {
  const normalizedA = normalizeForAnagram(a, options);
  const normalizedB = normalizeForAnagram(b, options);
  const freqA = letterFrequency(normalizedA);
  const freqB = letterFrequency(normalizedB);

  const letters = new Set<string>([...freqA.keys(), ...freqB.keys()]);
  const diffs: LetterDiff[] = [];
  for (const letter of [...letters].sort((x, y) => x.localeCompare(y))) {
    const countA = freqA.get(letter) ?? 0;
    const countB = freqB.get(letter) ?? 0;
    if (countA !== countB) {
      diffs.push({ letter, countA, countB });
    }
  }

  const isAnagram = normalizedA.length > 0 && normalizedB.length > 0 && diffs.length === 0;

  return { isAnagram, normalizedA, normalizedB, freqA, freqB, diffs };
}

/* ------------------------------------------------------------------ */
/* Handwriting                                                         */
/* ------------------------------------------------------------------ */

// Cursive-leaning stack built from fonts commonly installed on desktop OSes,
// with a generic cursive fallback so it degrades gracefully anywhere.
export const HANDWRITING_FONT_STACK =
  "'Segoe Script', 'Bradley Hand', 'Snell Roundhand', 'Comic Sans MS', cursive";
